<template>
	<div>
		<div class="flex flex-col lg:flex-row gap-2 justify-between mt-6 mb-2">
			<h2 class="whitespace-nowrap">Enter billing information</h2>
			<div class="flex gap-2 h-8">
				<img
					loading="lazy"
					src="~/assets/images/Visa_logo_2021.svg"
					alt="we accept visa"
					class="border border-grey-400 w-12"
				/>
				<img
					loading="lazy"
					src="~/assets/images/Mastercard_logo_2016.svg"
					alt="we accept mastercard"
					class="border border-grey-400 w-12"
				/>
				<img
					loading="lazy"
					src="~/assets/images/Discover_logo_2018.svg"
					alt="we accept discover"
					class="border border-grey-400 w-12"
				/>
				<img
					loading="lazy"
					src="~/assets/images/American_Express_logo_2018.svg"
					alt="we accept american express"
					class="border border-grey-400 w-12"
				/>
			</div>
		</div>
		<label class="checkbox" for="same_as" v-if="!requireDifferentAddress">
			<span class="checkbox__input">
				<input
					type="checkbox"
					id="same_as"
					name="checkbox"
					checked
					@click="toggleAddress"
				/>
				<span class="checkbox__control">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 24 24"
						aria-hidden="true"
						focusable="false"
					>
						<path
							fill="none"
							stroke="currentColor"
							stroke-width="3"
							d="M1.73 12.91l6.37 6.37L22.79 4.59"
						/>
					</svg>
				</span>
			</span>
			Billing address same as service location
		</label>
		<div v-if="differentAddress">
			<div class="w-full">
				<input
					type="text"
					v-model="staddress"
					name="address"
					placeholder="Street Address *"
					autocomplete="street-address"
					class="w-full"
					:class="{ error: validationErrors.includes('staddress') }"
					@keyup="smartystreets"
					@focus="clearValidationError('staddress')"
				/>

				<ul
					:class="{ 'display': showSmartyStreetsSuggestions ? 'block' : 'none' }"
				>
					<li
						v-for="(result, index) in results"
						:key="index"
						v-on:click="selectAddress(result)"
					>
						{{ result.street_line }}, {{ result.city }}, {{ result.state }}, {{ result.zipcode }}
					</li>
				</ul>
			</div>
			<div class="flex flex-col md:flex-row md:gap-4">
				<div class="w-full">
					<input
						type="text"
						v-model="city"
						name="city"
						placeholder="City *"
						autocomplete="address-level2"
						class="w-full text-sm md:text-base"
						:class="{ error: validationErrors.includes('city') }"
						@focus="clearValidationError('city')"
					/>
				</div>
				<div class="w-full">
					<select
						v-model="state"
						name="state"
						autocomplete="address-level1"
						class="w-full text-sm md:text-base"
						:class="{ error: validationErrors.includes('state') }"
						@focus="clearValidationError('state')"
					>
						<option value="" default selected>State *</option>
						<option value="AL">Alabama</option>
						<option value="AK">Alaska</option>
						<option value="AZ">Arizona</option>
						<option value="AR">Arkansas</option>
						<option value="CA">California</option>
						<option value="CO">Colorado</option>
						<option value="CT">Connecticut</option>
						<option value="DE">Delaware</option>
						<option value="DC">District Of Columbia</option>
						<option value="FL">Florida</option>
						<option value="GA">Georgia</option>
						<option value="HI">Hawaii</option>
						<option value="ID">Idaho</option>
						<option value="IL">Illinois</option>
						<option value="IN">Indiana</option>
						<option value="IA">Iowa</option>
						<option value="KS">Kansas</option>
						<option value="KY">Kentucky</option>
						<option value="LA">Louisiana</option>
						<option value="ME">Maine</option>
						<option value="MD">Maryland</option>
						<option value="MA">Massachusetts</option>
						<option value="MI">Michigan</option>
						<option value="MN">Minnesota</option>
						<option value="MS">Mississippi</option>
						<option value="MO">Missouri</option>
						<option value="MT">Montana</option>
						<option value="NE">Nebraska</option>
						<option value="NV">Nevada</option>
						<option value="NH">New Hampshire</option>
						<option value="NJ">New Jersey</option>
						<option value="NM">New Mexico</option>
						<option value="NY">New York</option>
						<option value="NC">North Carolina</option>
						<option value="ND">North Dakota</option>
						<option value="OH">Ohio</option>
						<option value="OK">Oklahoma</option>
						<option value="OR">Oregon</option>
						<option value="PA">Pennsylvania</option>
						<option value="RI">Rhode Island</option>
						<option value="SC">South Carolina</option>
						<option value="SD">South Dakota</option>
						<option value="TN">Tennessee</option>
						<option value="TX">Texas</option>
						<option value="UT">Utah</option>
						<option value="VT">Vermont</option>
						<option value="VA">Virginia</option>
						<option value="WA">Washington</option>
						<option value="WV">West Virginia</option>
						<option value="WI">Wisconsin</option>
						<option value="WY">Wyoming</option>
					</select>
				</div>

				<div class="w-full">
					<input
						v-model="zipCode"
						type="text"
						inputmode="numeric"
						pattern="\d{5}"
						maxlength="5"
						name="zipCode"
						placeholder="Zip Code *"
						autocomplete="postal-code"
						class="w-full text-sm md:text-base"
						:class="{ error: validationErrors.includes('zipCode') }"
						@focus="clearValidationError('zipCode')"
					/>
				</div>
			</div>
		</div>

		<div>
			<input
				type="email"
				v-model="customerEmail"
				name="customerEmail"
				placeholder="Email *"
				autocomplete="email"
				class="w-full text-sm md:text-base"
				required
				:class="{ error: validationErrors.includes('customerEmail') }"
				@focus="clearValidationError('customerEmail')"
			/>
		</div>

		<div v-if="requirePhone" class="flex flex-col md:flex-row gap-0 md:gap-4">
			<MaskInput
				ref="homePhoneInput"
				type="text"
				:mask="PHONE_INPUT_MASK"
				:placeholder="homePhonePlaceholder"
				autocomplete="tel"
				name="homePhone"
				v-model="homePhone"
				:value="homePhone"
				class="w-full text-sm md:text-base"
				:required="!isCellPhoneValid"
				:class="{ error: validationErrors.includes('homePhone') }"
				@focus="clearValidationError('homePhone')"
			/>

			<MaskInput
				ref="cellPhoneInput"
				type="text"
				:mask="PHONE_INPUT_MASK"
				:placeholder="cellPhonePlaceholder"
				autocomplete="tel"
				name="cellPhone"
				v-model="cellPhone"
				:value="cellPhone"
				class="w-full text-sm md:text-base"
				:required="!isHomePhoneValid"
				:class="{ error: validationErrors.includes('cellPhone') }"
				@focus="clearValidationError('cellPhone')"
			/>
		</div>

		<div v-show="showPayment">
			<input
				type="text"
				placeholder="Name on the card *"
				autocomplete="cc-name"
				v-model="name"
				class="w-full text-sm md:text-base"
				:class="{ error: validationErrors.includes('ccName') }"
				@focus="clearValidationError('ccName')"
			/>

			<div class="grid grid-cols-3 md:gap-4">
				<select
					v-model="creditCardType"
					name="creditCardType"
					autocomplete="cc-type"
					class="w-full md:w-auto text-sm md:text-base"
					:class="{ error: validationErrors.includes('ccType') }"
					@focus="clearValidationError('ccType')"
				>
					<option value="" default selected>Credit Card Type</option>
					<option value="visa">Visa</option>
					<option value="mastercard">Mastercard</option>
					<option value="discover">Discover</option>
					<option value="AX">American Express</option>
				</select>

				<div class="col-span-2">
					<InputObfuscatedToggleable
						v-model="ccNumber"
						maxlength="16"
						inputmode="numeric"
						placeholder="Card Number *"
						autocomplete="cc-number"
						required
						:inputClass="{
							'w-full text-sm md:text-base': true,
							error: validationErrors.includes('ccNumber'),
						}"
						@focus="clearValidationError('ccNumber')"
					/>
				</div>
			</div>

			<div class="flex flex-col md:flex-row md:gap-4">
				<select
					placeholder="Month"
					v-model="ccMonth"
					autocomplete="cc-exp-month"
					required
					class="w-full text-sm md:text-base"
					:class="{ error: validationErrors.includes('ccExpMonth') }"
					@focus="clearValidationError('ccExpMonth')"
				>
					<option value="" disabled selected>Month</option>
					<option value="01">January</option>
					<option value="02">February</option>
					<option value="03">March</option>
					<option value="04">April</option>
					<option value="05">May</option>
					<option value="06">June</option>
					<option value="07">July</option>
					<option value="08">August</option>
					<option value="09">September</option>
					<option value="10">October</option>
					<option value="11">November</option>
					<option value="12">December</option>
				</select>

				<select
					v-model="ccYear"
					placeholder="Year"
					autocomplete="cc-exp-year"
					required
					class="w-full text-sm md:text-base"
					:class="{ error: validationErrors.includes('ccExpYear') }"
					@focus="clearValidationError('ccExpYear')"
				>
					<option value="" disabled selected>Year</option>
					<option
						v-for="(year, index) in ccExpirationYearOptions"
						:key="index"
						:value="year"
					>
						{{ year }}
					</option>
				</select>

				<div class="w-full">
					<InputObfuscatedToggleable
						v-model="ccSecurityCode"
						pattern="\d{3,4}"
						inputmode="numeric"
						placeholder="Security Code *"
						autocomplete="cc-csc"
						:maxlength="ccSecurityCodeMaxLength"
						required
						:inputClass="{
							'w-full text-sm md:text-base': true,
							error: validationErrors.includes('ccSecurityCode'),
						}"
						@focus="clearValidationError('ccSecurityCode')"
					/>
				</div>
			</div>
		</div>

		<textarea
			class="w-full h-40"
			placeholder="Add a note or special directions"
			v-model="ccNote"
		></textarea>

		<div class="flex justify-center md:justify-start mb-4 hidden">
			<label class="checkbox" for="AllowText">
				<span class="checkbox__input">
					<input
						type="checkbox"
						id="AllowText"
						name="AllowText"
						v-model="AllowText"
					/>
					<span class="checkbox__control">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							aria-hidden="true"
							focusable="false"
						>
							<path
								fill="none"
								stroke="currentColor"
								stroke-width="3"
								d="M1.73 12.91l6.37 6.37L22.79 4.59"
							/>
						</svg>
					</span>
				</span>
				<p class="text-sm my-2">
					By checking this box, you agree to receive text messages (e.g. promotions, service notifications/reminders) from Spring Green at the number provided and consent to the monitoring and storage of the conversation.
					Consent is not a condition of purchase. Msg & data rates may apply. Msg frequency varies.
					Unsubscribe at any time by replying STOP or clicking the unsubscribe link (where available). HELP for assistance.
					See our <a href="/policies/privacy-policy" target="_blank">Privacy Policy</a> and our <a href="/policies/terms-use" target="_blank">Terms of Use</a>.
				</p>
			</label>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useStore } from "vuex";
import type { Store } from "vuex/types/index.js";
import type State from "~/domains/store/State";
import { MaskInput } from "vue-3-mask";
import { getUserInfo } from "~/utils/customer";
import {
	formatPhoneNumber,
	getCustomerPhoneNumber,
	normalizePhoneNumber,
	isPhoneNumberValid,
	PHONE_INPUT_MASK,
} from "~/utils/phone";
import type { SmartyStreetsLookupResult } from "~/domains/smartystreets/SmartyStreetsLookupResult";
import type { SmartyStreetsLookupSuggestion } from "~/domains/smartystreets/SmartyStreetsLookupSuggestion";

const props = defineProps({
	requirePhone: { type: Boolean, default: true },
	showPayment: { type: Boolean, default: true},
});

const store: Store<State> = useStore();

const validationErrors = ref<string[]>([]);
const name = ref("");
const ccNumber = ref("");
const ccMonth = ref("");
const ccExpirationYearOptions = ref<string[]>([]);
const ccYear = ref("");
const ccSecurityCode = ref("");
const ccNote = ref("");
const staddress = ref("");
const city = ref("");
const state = ref("");
const zipCode = ref("");
const creditCardType = ref("");
const results = ref<SmartyStreetsLookupSuggestion[]>([]);
const showSmartyStreetsSuggestions = ref(false);
const customerEmail = ref("");
const differentAddress = ref(false);
const requireDifferentAddress = ref(false);
const homePhone = ref("");
const cellPhone = ref("");
const AllowText = ref(false);

const ccSecurityCodeMaxLength = computed(() => {
	return creditCardType.value === "AX" ? 4 : 3;
});

const { $st } = useNuxtApp();

watch(
	() => store.state.persistedState.customer,
	(customer) => {
		homePhone.value = formatPhoneNumber(
			getCustomerPhoneNumber(customer, "Home"),
		) ?? "";
		cellPhone.value = formatPhoneNumber(
			getCustomerPhoneNumber(customer, "Cell"),
		) ?? "";
	},
	{ immediate: true },
);

onMounted(async () => {
	configureCCExpirationYearOptions();

	const persistedState = store.state.persistedState;
	const userInfo = getUserInfo(persistedState);

	// Pre-populate fields
	let customerEmailValue = "";
	if (userInfo.email) {
		//Parse out email
		if (userInfo.email.includes(";")) {
			customerEmailValue = userInfo.email.split(";")[0];
		} else if (userInfo.email.includes(",")) {
			customerEmailValue = userInfo.email.split(",")[0];
		} else {
			customerEmailValue = userInfo.email;
		}
	}

	if (validEmail(customerEmailValue)) {
		customerEmail.value = customerEmailValue;
	}

	let validInfo = true;

	if (!userInfo.address1) {
		validInfo = false;
	}
	if (!userInfo.city) {
		validInfo = false;
	}
	if (!userInfo.state) {
		validInfo = false;
	}
	if (!userInfo.zip) {
		validInfo = false;
	}

	staddress.value = userInfo.address1;
	city.value = userInfo.city;
	state.value = userInfo.state;
	zipCode.value = userInfo.zip;

	if (!validInfo) {
		requireDifferentAddress.value = true;
		differentAddress.value = true;
	}
});

const toggleAddress = () => {
	differentAddress.value = !differentAddress.value;
};

const configureCCExpirationYearOptions = () => {
	const yearOptions = [];
	const startYear = new Date().getFullYear();
	for (let i = 0; i < 10; i++) {
		yearOptions.push((startYear + i).toString());
	}
	ccExpirationYearOptions.value = yearOptions;
};

const isHomePhoneValid = computed(() => isPhoneNumberValid(homePhone.value));
const isCellPhoneValid = computed(() => isPhoneNumberValid(cellPhone.value));
const homePhonePlaceholder = computed(() => `Home Phone Number${isCellPhoneValid.value ? '' : ' *'}`);
const cellPhonePlaceholder = computed(() => `Cell Phone Number${isHomePhoneValid.value ? '' : ' *'}`);

const validate = () => {
	const errorsList = [];

	if (differentAddress.value) {
		if (!staddress.value) {
			errorsList.push("Street Address required.");
			validationErrors.value.push("staddress");
		}

		if (!city.value) {
			errorsList.push("City required.");
			validationErrors.value.push("city");
		}

		if (!state.value) {
			errorsList.push("State required.");
			validationErrors.value.push("state");
		}

		if (!zipCode.value) {
			errorsList.push("Zip code required.");
			validationErrors.value.push("zipCode");
		}
	}

	if (props.requirePhone) {
		if (!isHomePhoneValid.value && !isCellPhoneValid.value) {
			errorsList.push(`Valid home or cell phone required (${PHONE_INPUT_MASK}).`);
			validationErrors.value.push("homePhone");
			validationErrors.value.push("cellPhone");
		}
	}

	if (props.showPayment) {
		if (!name.value) {
			errorsList.push("Name required.");
			validationErrors.value.push("ccName");
		}

		if (!creditCardType.value) {
			errorsList.push("Credit Card Type required.");
			validationErrors.value.push("ccType");
		}

		if (
			!ccNumber.value?.trim().match(/^\d{16}$/) &&
			creditCardType.value != "AX"
		) {
			errorsList.push(
				"Credit Card Number required and must be valid with no dashes."
			);
			validationErrors.value.push("ccNumber");
		}

		if (
			!/^3[47][0-9]{13}$/.test(ccNumber.value?.trim()) &&
			creditCardType.value == "AX"
		) {
			errorsList.push(
				"Credit Card Number required and must be valid with no dashes."
			);
			validationErrors.value.push("ccNumber");
		}

		if (
			!ccMonth.value?.trim().match(/^\d{2}$/) ||
			Number(ccMonth.value?.trim()) > 12
		) {
			errorsList.push("Expiration Month required.");
			validationErrors.value.push("ccExpMonth");
		}

		if (!ccYear.value?.trim().match(/^\d{4}$/)) {
			errorsList.push("Expiration Year required.");
			validationErrors.value.push("ccExpYear");
		}

		if (
			!ccSecurityCode.value?.trim().match(/^\d{3,4}$/) ||
			ccSecurityCode.value?.trim().length < ccSecurityCodeMaxLength.value
		) {
			errorsList.push("Security Code required and must be valid.");
			validationErrors.value.push("ccSecurityCode");
		}

		const today = new Date();
		if (
			today.getFullYear() > Number(ccYear.value) ||
			(today.getFullYear() == Number(ccYear.value) &&
				today.getMonth() > Number(ccMonth.value))
		) {
			errorsList.push("Expiration must be valid.");
		}
	}

	if (!validEmail(customerEmail.value?.trim())) {
		errorsList.push("Email must be valid.");
		validationErrors.value.push("customerEmail");
	}

	return errorsList;
};

const clearValidationError = (fieldName: string) => {
	const errorIndex = validationErrors.value.indexOf(fieldName);
	if (errorIndex !== -1) {
		validationErrors.value.splice(errorIndex, 1);
	}
};

const validEmail = (email: string) => {
	if (email != null) {
		const re = /^\S+@\S+\.\S+$/;
		return re.test(email);
	}
	return false;
};

const smartystreets = async () => {
	if (staddress.value?.length > 0) {
		const resultsValue: SmartyStreetsLookupResult = await $st.lookup(staddress.value);

		if (Object.keys(resultsValue).length > 0) {
			results.value = resultsValue.suggestions;
			showSmartyStreetsSuggestions.value = true;
		}
	}
};

const selectAddress = (suggestion: SmartyStreetsLookupSuggestion) => {
	staddress.value = suggestion.street_line;
	city.value = suggestion.city;
	state.value = suggestion.state;
	zipCode.value = suggestion.zipcode;

	showSmartyStreetsSuggestions.value = false;
};

const getBillingInfo = () => {
	const errorsList = validate();
	if (errorsList.length > 0) return undefined;

	const userInfo = getUserInfo(store.state.persistedState);

	return {
		name: name.value?.trim(),
		address1: (differentAddress.value ? staddress.value : userInfo.address1)?.trim(),
		city: (differentAddress.value ? city.value : userInfo.city)?.trim(),
		state: (differentAddress.value ? state.value : userInfo.state)?.trim(),
		zip: (differentAddress.value ? zipCode.value : userInfo.zip)?.trim(),
		email: customerEmail.value?.trim(),
		homePhone: normalizePhoneNumber(homePhone.value) ?? "",
		cellPhone: normalizePhoneNumber(cellPhone.value) ?? "",
		AllowText: AllowText.value,
		ccType: creditCardType.value?.trim(),
		ccNumber: ccNumber.value?.trim(),
		ccMonth: ccMonth.value?.trim(),
		ccYear: ccYear.value?.trim(),
		ccSecurityCode: ccSecurityCode.value?.trim(),
		note: ccNote.value,
	};
};

defineExpose({ validate, getBillingInfo });
</script>


<style scoped>
.checkbox {
	display: grid;
	grid-template-columns: min-content auto;
	grid-gap: 0.5em;
}

.checkbox__control {
	display: inline-grid;
	width: 1em;
	height: 1em;
	border-radius: 0.25em;
	border: 0.1em solid #00810e;
	background: #fff;
	color: white;
	margin-top: -3px;
}

.checkbox__control svg {
	transition: transform 0.1s ease-in 25ms;
	transform: scale(0);
	transform-origin: bottom left;
}

.checkbox__input {
	display: grid;
	grid-template-areas: "checkbox";
}

.checkbox__input input {
	opacity: 0;
	width: 0;
	height: 0;
}

.checkbox__input input:checked + .checkbox__control svg {
	transform: scale(1);
}

.checkbox__input input:checked + .checkbox__control {
	background: #00810e;
}
</style>
